import { Printer } from 'lucide-react'
import { toast } from 'sonner'

import { FormEvent, useState } from 'react'

import { Card, CardContent, CardHeader, CardTitle, Input, Label, PosButton, Segmented, Spinner } from '@/components/ui'
import { useSession } from '@/contexts/SessionContext'
import { PageHeader } from '@/components/pos/PageHeader'
import { CreditRepaymentCard } from '@/components/pos/checkout/CreditRepaymentCard'
import { useCloseShift, useOpenShift, useRecordCash, useShift } from '@/hooks/features/shift'
import { ShiftEvent, ShiftSummary } from '@/db/repositories/shifts'
import { reportSections, usePrinter } from '@/services/printer'
import { formatMoney } from '@/utils'

const amountOf = (form: FormData, name: string) => parseFloat(String(form.get(name) ?? '')) || 0

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={strong ? 'font-semibold tabular-nums text-ink' : 'tabular-nums text-ink'}>{value}</span>
  </div>
)

/** The X report while the shift is open, the Z report when it closes (§6.2). Printed from the same rows. */
const Report = ({ summary, title, onPrint }: { summary: ShiftSummary; title: string; onPrint: () => void }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0">
      <CardTitle>{title}</CardTitle>
      <PosButton variant="outline" onClick={onPrint}>
        <Printer /> Print
      </PosButton>
    </CardHeader>
    <CardContent className="divide-y divide-border-subtle">
      {reportSections(summary).map((rows, i) => (
        <div key={i} className="py-2 first:pt-0">
          {rows.map((r) => (
            <Row key={r.label} {...r} />
          ))}
        </div>
      ))}
    </CardContent>
  </Card>
)

const NOTES = [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1]

const Tiles = ({ summary }: { summary: ShiftSummary }) => {
  const tiles = [
    { label: 'Opening float', value: formatMoney(Number(summary.shift.opening_balance ?? 0)) },
    { label: 'Cash sales', value: formatMoney(summary.sales.cash) },
    { label: 'Cash in / out', value: `${formatMoney(summary.cashIn)} / ${formatMoney(summary.cashOut)}` },
    { label: 'Cash refunds', value: formatMoney(summary.cashRefunds) },
    { label: 'Expected in drawer', value: formatMoney(summary.expectedCash), strong: true },
  ]
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-md border border-border bg-card px-4 py-3">
          <p className={t.strong ? 'text-lg font-semibold tabular-nums text-success-dark' : 'text-lg font-semibold tabular-nums text-ink'}>{t.value}</p>
          <p className="text-xs text-muted-foreground">{t.label}</p>
        </div>
      ))}
    </div>
  )
}

const time = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

const Activity = ({ events }: { events: ShiftEvent[] }) => (
  <Card>
    <CardHeader>
      <CardTitle>Activity</CardTitle>
    </CardHeader>
    <CardContent>
      <ol className="max-h-80 space-y-1.5 overflow-y-auto text-sm">
        {events.map((e, i) => (
          <li key={`${e.at}-${i}`} className="flex gap-3">
            <span className="w-12 shrink-0 tabular-nums text-muted-foreground">{time(e.at)}</span>
            <span className="min-w-0 flex-1 truncate text-ink">{e.text}</span>
            {e.amount !== undefined && <span className="tabular-nums text-ink">{formatMoney(e.amount)}</span>}
          </li>
        ))}
      </ol>
    </CardContent>
  </Card>
)

/** Counted cash as the sum of the notes and coins in the drawer, so a difference can be traced to the count. */
const NoteCount = ({ onTotal }: { onTotal: (total: number) => void }) => {
  const [counts, setCounts] = useState<Record<number, string>>({})
  const total = NOTES.reduce((s, n) => s + n * (parseInt(counts[n] ?? '', 10) || 0), 0)
  return (
    <details className="rounded-md border border-border-subtle px-3 py-2">
      <summary className="cursor-pointer text-sm text-text-2">Count by note</summary>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
        {NOTES.map((n) => (
          <label key={n} className="space-y-1 text-xs text-muted-foreground">
            <span>৳{n} ×</span>
            <Input
              inputMode="numeric"
              value={counts[n] ?? ''}
              onChange={(e) => {
                const next = { ...counts, [n]: e.target.value.replace(/\D/g, '') }
                setCounts(next)
                onTotal(NOTES.reduce((s, m) => s + m * (parseInt(next[m] ?? '', 10) || 0), 0))
              }}
              className="h-10 text-right tabular-nums"
            />
          </label>
        ))}
      </div>
      <p className="mt-2 text-right text-sm text-ink">
        Total <span className="font-semibold tabular-nums">{formatMoney(total)}</span>
      </p>
    </details>
  )
}

/** Open and close the shift, move cash in and out of the drawer, and see the running totals. */
export const ShiftPage = () => {
  const { can } = useSession()
  const cashTypes = (['cash_in', 'cash_out'] as const).filter((t) => can(`pos:${t}`))
  const { data: summary, isLoading } = useShift()
  const open = useOpenShift()
  const record = useRecordCash()
  const close = useCloseShift()
  const [cashType, setCashType] = useState<'cash_in' | 'cash_out'>(cashTypes[0] ?? 'cash_in')
  const [closed, setClosed] = useState<ShiftSummary | null>(null)
  const [counted, setCounted] = useState('')
  const { printShiftReport } = usePrinter()

  const onOpen = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const float = amountOf(new FormData(e.currentTarget), 'float')
    open.mutate(float, {
      onSuccess: () => {
        setClosed(null)
        toast.success('Shift opened')
      },
      onError: (err) => toast.error(err.message),
    })
  }

  const onCash = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)
    const amount = amountOf(data, 'amount')
    if (amount <= 0) return
    record.mutate(
      { type: cashType, amount, remark: String(data.get('remark') ?? '').trim() || undefined },
      {
        onSuccess: () => {
          form.reset()
          toast.success(cashType === 'cash_in' ? 'Cash added' : 'Cash removed')
        },
        onError: (err) => toast.error(err.message),
      }
    )
  }

  const onClose = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const counted = amountOf(new FormData(e.currentTarget), 'counted')
    close.mutate(counted, {
      onSuccess: (z) => {
        setClosed(z)
        setCounted('')
        printShiftReport(z, { auto: true })
      },
      onError: (err) => toast.error(err.message),
    })
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Shift" subtitle="Open the drawer, move cash, and close with a count." />
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        {isLoading ? (
          <Spinner />
        ) : !summary ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Open a shift</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={onOpen} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="float">Cash in the drawer now</Label>
                    <Input id="float" name="float" inputMode="decimal" defaultValue="0" className="h-11 text-right tabular-nums" />
                  </div>
                  <PosButton type="submit" size="touch" className="w-full" disabled={open.isPending}>
                    Open shift
                  </PosButton>
                </form>
              </CardContent>
            </Card>
            {closed && <Report summary={closed} title="Last shift (Z report)" onPrint={() => printShiftReport(closed)} />}
          </div>
        ) : (
          <div className="space-y-4">
          <Tiles summary={summary} />
          <div className="grid gap-4 lg:grid-cols-2">
            <Report summary={summary} title="This shift (X report)" onPrint={() => printShiftReport(summary)} />
            <div className="space-y-4">
              {cashTypes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{cashTypes.length === 2 ? 'Cash in / out' : cashTypes[0] === 'cash_in' ? 'Cash in' : 'Cash out'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={onCash} className="space-y-3">
                    {cashTypes.length === 2 && (
                      <Segmented<'cash_in' | 'cash_out'>
                        value={cashType}
                        onChange={setCashType}
                        options={[
                          { value: 'cash_in', label: 'Cash in' },
                          { value: 'cash_out', label: 'Cash out' },
                        ]}
                      />
                    )}
                    <Input name="amount" inputMode="decimal" placeholder="Amount" className="h-11 text-right tabular-nums" />
                    <Input name="remark" placeholder="Reason (optional)" className="h-11" />
                    <PosButton type="submit" size="touch" variant="secondary" className="w-full" disabled={record.isPending}>
                      Record
                    </PosButton>
                  </form>
                </CardContent>
              </Card>
              )}
              {can('pos:manage_credit') && <CreditRepaymentCard />}
              {can('pos:close_shift') && (
              <Card>
                <CardHeader>
                  <CardTitle>Close shift</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={onClose} className="space-y-3">
                    <Label htmlFor="counted">Cash counted in the drawer</Label>
                    <Input id="counted" name="counted" inputMode="decimal" required value={counted} onChange={(e) => setCounted(e.target.value)} className="h-11 text-right tabular-nums" />
                    <NoteCount onTotal={(t) => setCounted(t ? String(t) : '')} />
                    <PosButton type="submit" size="touch" variant="destructive" className="w-full" disabled={close.isPending}>
                      Close shift
                    </PosButton>
                  </form>
                </CardContent>
              </Card>
              )}
              <Activity events={summary.activity} />
            </div>
          </div>
          </div>
        )}
      </div>
    </div>
  )
}
